import clsx from 'clsx';
import { PropsWithChildren, useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

import { Spacer } from '~/_legacy/components/atoms/spacer';
import { FormButton, FormInput } from '~/_legacy/components/forms';

import { COLOR_TEXT_MAIN } from '~/colors/text';
import { useAsyncEffect } from '~/hooks/useAsyncEffect';
import { useEscapeKey } from '~/hooks/useEscapeKey';
import { media } from '~/utils';
import { sleep } from '~/utils/sleep';
import { HtmlTagProps } from '~/utils/types/HtmlTagProps';

import CheckIcon from './_icons/check.svg';

import CloseIcon from '@public/icons/close-simple.svg';
import LoadingCircleIcon from '@public/icons/loading-circle.svg';

export type SideModalProps = {
  title?: string;
  description?: string;
  width?: string;
  onClose?: () => void;
  onComplete?: () => void | Promise<void>;
  isDisabledComplete?: boolean;
  isLoading?: boolean;
  /**
   * - 指定した場合のみ検索用のテキスト入力のinputが表示される
   * - 引数は検索単語をスペース(全角含む)で分けた配列
   *   - 空文字列は含めない
   *   - 英単語は`toLowerCase()`で判定する
   */
  onChangeSearchText?: (_: string[]) => void;
  onChange?: () => void;
  searchPlaceholder?: string;
};

type Props = PropsWithChildren & SideModalProps;

export function SideModal(props: Props) {
  const [show, setShow] = useState(false);
  useAsyncEffect(async () => {
    await sleep(50);
    setShow(true);
  }, []);

  const onClose = async () => {
    setShow(false);
    await sleep(ANIMATION_TIME + 50);
    props.onClose?.();
  };

  useEscapeKey(onClose);

  const hasOnComplete = Boolean(props.onComplete);
  const onComplete = async () => {
    if (props.onComplete) {
      await props.onComplete();
    }
    await onClose();
  };

  const [searchText, setSearchText] = useState('');
  const onChangeSearch: HtmlTagProps['input']['onChange'] = (e) => {
    // NOTE: stopPropagationしないと変なonChangeしてしまう
    e.stopPropagation();
    setSearchText(e.target.value);
  };

  const timeoutRef = useRef<NodeJS.Timeout>();
  // キーボードの入力による多重renderingを防ぐために、入力してから500ms待ってからonChangeSearchTextする
  useEffect(() => {
    if (!props.onChangeSearchText) return;
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      const searchValues = searchText
        .split(/\s/)
        .filter(Boolean)
        .map((it) => it.toLowerCase());
      props.onChangeSearchText?.(searchValues);
    }, 500);
    return () => clearTimeout(timeoutRef.current);
  }, [searchText]);

  return (
    <StyledFixed className={clsx('disable-global-scroll', { SideModal__hide: !show })}>
      <StyledBackdrop onClick={onClose} />
      <StyledContentContainer width={props.width}>
        <StyledHeader>
          <StyledHeaderButtons>
            <FormButton
              width='fit-content'
              rounded
              variant='ghost'
              size='xs'
              disabled={props.isLoading}
              icon={<CloseIcon width='2em' height='2em' />}
              onClick={onClose}
            >
              閉じる
            </FormButton>
            <Spacer />
            {props.isLoading && <LoadingCircleIcon />}
            {hasOnComplete && (
              <FormButton
                width='80px'
                rounded
                size='xs'
                disabled={props.isDisabledComplete || props.isLoading}
                icon={<CheckIcon width='2.5em' height='2.5em' />}
                onClick={onComplete}
              >
                確定
              </FormButton>
            )}
          </StyledHeaderButtons>
          <StyledHeaderInfo>
            <h1>{props.title}</h1>
            {props.description && <h2>{props.description}</h2>}
            {props.onChangeSearchText && (
              <StyledFormInput
                noErrorSpace
                value={searchText}
                onChange={onChangeSearch}
                placeholder={props.searchPlaceholder}
              />
            )}
          </StyledHeaderInfo>
        </StyledHeader>
        <StyledContent>{props.children}</StyledContent>
      </StyledContentContainer>
    </StyledFixed>
  );
}

const ANIMATION_TIME = 450;

const cssHeight100vh = css`
  height: 100dvh;
  height: 100vh;
`;

const StyledFixed = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  ${cssHeight100vh}
  z-index: 2100; // header以上
`;

const StyledBackdrop = styled.div`
  width: 100vw;
  ${cssHeight100vh}
  background-color: rgba(0, 0, 0, 40%);
  backdrop-filter: blur(8px);
  transition: ${ANIMATION_TIME}ms;
  .SideModal__hide & {
    background-color: transparent;
    backdrop-filter: none;
  }
`;

const StyledContentContainer = styled.div<Pick<Props, 'width'>>`
  position: fixed;
  top: 0;
  right: 0;
  width: ${(props) => props.width ?? '400px'};
  max-width: calc(100vw - 16px);
  ${cssHeight100vh}
  background-color: #d5d5d5;
  display: flex;
  flex-direction: column;

  transition: ${ANIMATION_TIME}ms;
  opacity: 1;
  .SideModal__hide & {
    transform: translateX(75%);
    opacity: 0;
  }
`;

const StyledHeader = styled.div`
  padding-top: 32px;
  background-color: #ebebeb;
  ${media.sp`
    padding-top: 16px;
  `}
`;

const StyledHeaderButtons = styled.div`
  margin: 0 4px;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const StyledHeaderInfo = styled.div`
  margin: 0 16px;
  margin-top: 12px;
  padding-bottom: 16px;
  display: grid;
  grid-auto-rows: auto;
  gap: 8px;

  > h1 {
    font-size: 18px;
    font-weight: bold;
    color: #4d4d4d;
  }
  > h2 {
    font-size: 12px;
    color: ${COLOR_TEXT_MAIN};
  }
`;

const StyledFormInput = styled(FormInput)`
  margin-top: 4px;
  input {
    padding: 6px 12px;
    border-radius: 12px;
  }
`;

const StyledContent = styled.div`
  padding: 8px 16px;
  height: 100%;
  overflow-y: auto;
`;
